import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstaller = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Vérifier si l'app est déjà installée (mode standalone)
    const checkStandalone = () => {
      return window.matchMedia('(display-mode: standalone)').matches ||
             window.navigator.standalone ||
             document.referrer.includes('android-app://');
    };

    setIsStandalone(checkStandalone());

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      // console.log('PWA install prompt triggered');
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      
      // Afficher la bannière après un délai pour ne pas être intrusif
      setTimeout(() => {
        if (!checkStandalone()) {
          setShowInstallBanner(true);
        }
      }, 3000);
    };

    // Écouter l'installation réussie
    const handleAppInstalled = () => {
      // console.log('PWA installed successfully');
      setShowInstallBanner(false);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      // Afficher le prompt d'installation
      deferredPrompt.prompt();
      
      // Attendre la réponse de l'utilisateur
      const { outcome } = await deferredPrompt.userChoice;
      
      // console.log(`User response to install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        // console.log('User accepted the install prompt');
      }
      
      // Nettoyer
      setDeferredPrompt(null);
      setShowInstallBanner(false);
      setIsInstallable(false);
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    // Ne pas reproposer pendant cette session
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Ne rien afficher si déjà installé ou si le banner a été refusé
  if (isStandalone || 
      !showInstallBanner || 
      !isInstallable ||
      sessionStorage.getItem('pwa-banner-dismissed')) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-500">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl shadow-2xl border border-orange-300/30 backdrop-blur-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-start space-x-3 flex-1">
            <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm sm:text-base leading-tight">
                📱 Installer Korean Lash Lift by Ali
              </h3>
              <p className="text-white/90 text-xs sm:text-sm leading-tight mt-1">
                Accès rapide depuis votre écran d'accueil
              </p>
              
              {/* Avantages de l'installation */}
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/80">
                <span className="bg-white/10 rounded-full px-2 py-1">⚡ Plus rapide</span>
                <span className="bg-white/10 rounded-full px-2 py-1">📱 Hors ligne</span>
                <span className="bg-white/10 rounded-full px-2 py-1">🔔 Notifications</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-3">
            <button
              onClick={handleInstall}
              className="bg-white/20 hover:bg-white/30 border border-white/30 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105 flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Installer</span>
            </button>
            
            <button
              onClick={dismissBanner}
              className="text-white/80 hover:text-white p-1 transition-colors"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Indicateur de progression pour auto-dismiss */}
        <div className="h-1 bg-white/10 overflow-hidden">
          <div 
            className="h-full bg-white/30 transition-all duration-[10000ms] ease-linear transform origin-left"
            style={{ 
              transform: showInstallBanner ? 'scaleX(0)' : 'scaleX(1)',
              animation: showInstallBanner ? 'shrink 10s linear forwards' : 'none'
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes shrink {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
      `}</style>
    </div>
  );
};

export default PWAInstaller;